/**
 * Хувь зээл — дэлгүүрийн өөрийн зээл (эзэмшигчийн шийдвэр 2026-09-28).
 *
 *   · Хүү, нэмэгдэл БАЙХГҮЙ — гутлын үнэ хэвээр.
 *   · Худалдан авагч ЯГ 10%-ийг эхэлж төлнө (бэлэн, карт, QPay, шилжүүлэг);
 *     үлдсэн 90% нь `INSTALLMENT` төлбөрийн мөр болж авлага болно.
 *   · Хугацаа 1–6 сар, сар бүр ижил дүн (үлдэгдэл сүүлийн сард).
 *   · Зээл НЭЭЛТТЭЙ байх хооронд тэр гүйлгээг буцаахгүй.
 *
 * Сервер (`order.confirm`, `/installments`), POS, вэб ГУРВУУЛАА эндээс
 * бодно — нэг газар дүрэм, клиент «болно» гэж үзээд сервер татгалзахгүй.
 * Мөнгө decimal.js-ээр, төгрөг бүхэл (дүрэм 3).
 */
import { Decimal } from 'decimal.js';
import { ulaanbaatarDateParts } from './serial.js';
export const INSTALLMENT_METHOD = 'INSTALLMENT';
/** Эхний төлөлт — нийт дүнгийн хувь. Өөрчлөхөд тестийг хамт зас. */
export const INSTALLMENT_DOWN_PCT = 10;
export const INSTALLMENT_TERM_MIN = 1;
export const INSTALLMENT_TERM_MAX = 6;
/** Эхний болон сар бүрийн төлөлтийг ЭДГЭЭРЭЭР л хийнэ — зээлээр зээл төлөхгүй. */
export const INSTALLMENT_PAY_METHODS = ['CASH', 'CARD', 'QPAY', 'TRANSFER'];
const whole = (d) => d.toDecimalPlaces(0, Decimal.ROUND_HALF_UP);
export function isInstallmentTerm(months) {
    return Number.isInteger(months) && months >= INSTALLMENT_TERM_MIN && months <= INSTALLMENT_TERM_MAX;
}
/** Нийт төлөх дүнг эхний төлөлт (10%, бүхэл төгрөг) ба зээлийн дүн болгоно. */
export function installmentSplit(totalMnt) {
    const total = new Decimal(totalMnt);
    const downMnt = whole(total.times(INSTALLMENT_DOWN_PCT).dividedBy(100));
    return { downMnt, principalMnt: total.minus(downMnt) };
}
/**
 * Хувь зээлийн захиалгын төлбөр ЯГ хоёр мөр: эхний 10% (зөвшөөрөгдсөн
 * хэлбэрээр) + үлдсэн 90% `INSTALLMENT`. Зөв бол `null`.
 */
export function checkInstallmentPayments(parts, totalMnt) {
    const loan = parts.filter((p) => p.method === INSTALLMENT_METHOD);
    const down = parts.filter((p) => p.method !== INSTALLMENT_METHOD);
    if (parts.length !== 2 || loan.length !== 1 || down.length !== 1)
        return 'NOT_TWO_PARTS';
    if (!INSTALLMENT_PAY_METHODS.includes(down[0].method))
        return 'DOWN_METHOD';
    const { downMnt, principalMnt } = installmentSplit(totalMnt);
    if (!new Decimal(down[0].amountMnt).equals(downMnt))
        return 'DOWN_AMOUNT';
    if (!new Decimal(loan[0].amountMnt).equals(principalMnt))
        return 'PRINCIPAL_AMOUNT';
    return null;
}
/** УБ-ын өдрөөс `months` сарын дараах өдөр; 31 байхгүй сард сарын сүүлийн өдөр. */
function addMonths(from, months) {
    const idx = from.month - 1 + months;
    const year = from.year + Math.floor(idx / 12);
    const month = (idx % 12) + 1;
    const last = new Date(Date.UTC(year, month, 0)).getUTCDate();
    const day = Math.min(from.day, last);
    return `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
}
/**
 * Сар бүрийн төлөлтийн хуваарь. Дүн нь зээл ÷ сар (бүхэл төгрөг),
 * бутархайн үлдэгдэл СҮҮЛИЙН сард — нийлбэр нь зээлийн дүнтэй яг тэнцэнэ.
 * Эхний төлөлт зээл нээгдсэнээс нэг сарын дараа.
 */
export function installmentSchedule(principalMnt, termMonths, openedAt) {
    if (!isInstallmentTerm(termMonths)) {
        throw new Error(`installmentSchedule: хугацаа ${INSTALLMENT_TERM_MIN}–${INSTALLMENT_TERM_MAX} сар, ирсэн: ${termMonths}`);
    }
    const principal = new Decimal(principalMnt);
    const monthly = principal.dividedBy(termMonths).toDecimalPlaces(0, Decimal.ROUND_DOWN);
    const start = ulaanbaatarDateParts(openedAt);
    return Array.from({ length: termMonths }, (_, i) => ({
        no: i + 1,
        dueDate: addMonths(start, i + 1),
        amountMnt: (i === termMonths - 1 ? principal.minus(monthly.times(termMonths - 1)) : monthly).toFixed(2),
    }));
}
/**
 * Зээлийн байдал — хүчингүй болсон төлөлт тооцогдохгүй. `today` нь
 * Улаанбаатарын өдрөөр харьцуулагдана (хуваарийн огноотой ижил).
 */
export function installmentStatus(loan, payments, today) {
    const principal = new Decimal(loan.principalMnt);
    const paid = payments.filter((p) => !p.voided).reduce((a, p) => a.plus(p.amountMnt), new Decimal(0));
    const balance = Decimal.max(0, principal.minus(paid));
    const schedule = installmentSchedule(principal, loan.termMonths, loan.openedAt);
    const t = ulaanbaatarDateParts(today);
    const todayKey = `${t.year}-${String(t.month).padStart(2, '0')}-${String(t.day).padStart(2, '0')}`;
    let dueByToday = new Decimal(0);
    let cumulative = new Decimal(0);
    let nextDue = null;
    for (const d of schedule) {
        cumulative = cumulative.plus(d.amountMnt);
        if (d.dueDate <= todayKey)
            dueByToday = cumulative;
        if (!nextDue && cumulative.greaterThan(paid))
            nextDue = d;
    }
    const overdue = Decimal.max(0, dueByToday.minus(paid));
    return {
        paidMnt: paid.toFixed(2),
        balanceMnt: balance.toFixed(2),
        overdueMnt: overdue.toFixed(2),
        overdue: overdue.greaterThan(0),
        paidOff: balance.isZero(),
        nextDue,
    };
}
//# sourceMappingURL=installment.js.map